import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import config from '../config';

interface IdentityRevealContextType {
  revealIdentity: boolean;
  remainingSeconds: number;
  revealMode: 'always_visible' | 'optional_toggle' | 'always_masked';
  canToggleReveal: boolean;
  toggleRevealIdentity: (enabled: boolean, reason?: string) => Promise<void>;
}

const STORAGE_KEY = 'xrank_identity_reveal_enabled';
const STORAGE_EXPIRY_KEY = 'xrank_identity_reveal_expires_at';
const DEFAULT_DURATION_MS = 30 * 60 * 1000;

const IdentityRevealContext = createContext<IdentityRevealContextType | null>(null);

function readInitialState(): { enabled: boolean; expiresAt: number } {
  if (typeof window === 'undefined') return { enabled: false, expiresAt: 0 };

  const enabledRaw = sessionStorage.getItem(STORAGE_KEY);
  const expiryRaw = sessionStorage.getItem(STORAGE_EXPIRY_KEY);
  const enabled = enabledRaw === 'true';
  const expiresAt = Number(expiryRaw || '0');

  if (!enabled || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_EXPIRY_KEY);
    return { enabled: false, expiresAt: 0 };
  }

  return { enabled: true, expiresAt };
}

export const useIdentityReveal = (): IdentityRevealContextType => {
  const ctx = useContext(IdentityRevealContext);
  if (!ctx) throw new Error('useIdentityReveal must be used within IdentityRevealProvider');
  return ctx;
};

export const IdentityRevealProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken, user } = useAuth();
  const { org } = useTenant();
  const initial = useMemo(() => readInitialState(), []);

  const [revealIdentity, setRevealIdentity] = useState<boolean>(initial.enabled);
  const [expiresAt, setExpiresAt] = useState<number>(initial.expiresAt);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    initial.enabled ? Math.max(0, Math.floor((initial.expiresAt - Date.now()) / 1000)) : 0,
  );

  const revealMode = useMemo<'always_visible' | 'optional_toggle' | 'always_masked'>(() => {
    const raw = org?.settings?.identityRevealMode;
    if (raw === 'always_masked' || raw === 'optional_toggle' || raw === 'always_visible') {
      return raw;
    }
    return 'always_visible';
  }, [org?.settings?.identityRevealMode]);

  const canToggleReveal = revealMode === 'optional_toggle';

  const writeStorage = useCallback((enabled: boolean, expiry: number) => {
    if (enabled) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      sessionStorage.setItem(STORAGE_EXPIRY_KEY, String(expiry));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_EXPIRY_KEY);
    }
  }, []);

  const logToggle = useCallback(async (enabled: boolean, reason?: string, expiry?: number) => {
    try {
      const token = await getToken();
      if (!token) return;

      await fetch(`${config.API_BASE_URL}/api/identity-reveal/toggle`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled,
          reason: reason || (enabled ? 'manual_toggle_on' : 'manual_toggle_off'),
          expiresAt: expiry ? new Date(expiry).toISOString() : null,
        }),
      });
    } catch {
      // Logging must never block user interactions.
    }
  }, [getToken]);

  const toggleRevealIdentity = useCallback(async (enabled: boolean, reason?: string) => {
    if (!canToggleReveal) {
      return;
    }

    if (!enabled) {
      setRevealIdentity(false);
      setExpiresAt(0);
      setRemainingSeconds(0);
      writeStorage(false, 0);
      await logToggle(false, reason || 'manual_toggle_off');
      return;
    }

    const nextExpiry = Date.now() + DEFAULT_DURATION_MS;
    setRevealIdentity(true);
    setExpiresAt(nextExpiry);
    setRemainingSeconds(Math.floor(DEFAULT_DURATION_MS / 1000));
    writeStorage(true, nextExpiry);
    await logToggle(true, reason || 'manual_toggle_on', nextExpiry);
  }, [canToggleReveal, logToggle, writeStorage]);

  useEffect(() => {
    if (revealMode === 'always_visible') {
      setRevealIdentity(true);
      setExpiresAt(0);
      setRemainingSeconds(0);
      writeStorage(false, 0);
      return;
    }

    if (revealMode === 'always_masked') {
      setRevealIdentity(false);
      setExpiresAt(0);
      setRemainingSeconds(0);
      writeStorage(false, 0);
      return;
    }

    const next = readInitialState();
    setRevealIdentity(next.enabled);
    setExpiresAt(next.expiresAt);
    setRemainingSeconds(next.enabled ? Math.max(0, Math.floor((next.expiresAt - Date.now()) / 1000)) : 0);
  }, [revealMode, writeStorage]);

  useEffect(() => {
    if (!revealIdentity || !expiresAt) return;

    const timer = window.setInterval(() => {
      const seconds = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemainingSeconds(seconds);

      if (seconds <= 0) {
        setRevealIdentity(false);
        setExpiresAt(0);
        setRemainingSeconds(0);
        writeStorage(false, 0);
        void logToggle(false, 'session_expired');
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [expiresAt, revealIdentity, writeStorage, logToggle]);

  useEffect(() => {
    // User switched / signed out: reset reveal state.
    if (!user) {
      setRevealIdentity(false);
      setExpiresAt(0);
      setRemainingSeconds(0);
      writeStorage(false, 0);
    }
  }, [user, writeStorage]);

  const value = useMemo(
    () => ({ revealIdentity, remainingSeconds, revealMode, canToggleReveal, toggleRevealIdentity }),
    [revealIdentity, remainingSeconds, revealMode, canToggleReveal, toggleRevealIdentity],
  );

  return <IdentityRevealContext.Provider value={value}>{children}</IdentityRevealContext.Provider>;
};
